import { Chip, IconButton, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  Lock as LockIcon,
  ToggleOff as DeactivateIcon,
  ToggleOn as ActivateIcon,
} from '@mui/icons-material';
import type { ColumnDef } from '../common/tableFeatures';
import { StatusChip } from '../common/StatusChip';
import { clipped, dateSortValue, dateTime } from '../common/tableCells';
import type { User } from '../../types/api';

const roleColor = (role: string) => {
  switch (role) {
    case 'admin': return 'error' as const;
    case 'operator': return 'warning' as const;
    case 'viewer': return 'default' as const;
    default: return 'default' as const;
  }
};

interface UserColumnHandlers {
  onEdit: (user: User) => void;
  onChangePassword: (user: User) => void;
  onDeactivate: (username: string) => void;
  onReactivate: (username: string) => void;
}

/** TanStack column definitions for the user table. */
export const createUserColumns = ({
  onEdit,
  onChangePassword,
  onDeactivate,
  onReactivate,
}: UserColumnHandlers): ColumnDef<User>[] => [
  {
    id: 'username',
    accessorKey: 'username',
    header: 'Username',
    cell: ({ row }) => clipped(row.original.username, 160),
    size: 170,
  },
  {
    id: 'full_name',
    accessorFn: (row) => row.full_name || '',
    header: 'Full Name',
    cell: ({ row }) => clipped(row.original.full_name, 180),
    size: 190,
  },
  {
    id: 'email',
    accessorFn: (row) => row.email || '',
    header: 'Email',
    cell: ({ row }) => clipped(row.original.email, 200),
    size: 220,
  },
  {
    id: 'role',
    accessorKey: 'role',
    header: 'Role',
    cell: ({ row }) => (
      <Chip
        label={row.original.role.toUpperCase()}
        color={roleColor(row.original.role)}
        size="small"
      />
    ),
    size: 110,
  },
  {
    id: 'is_active',
    accessorFn: (row) => (row.is_active ? 1 : 0),
    header: 'Status',
    cell: ({ row }) => (
      <StatusChip status={row.original.is_active ? 'active' : 'inactive'} />
    ),
    size: 110,
  },
  {
    id: 'is_ldap',
    accessorFn: (row) => (row.is_ldap_user ? 1 : 0),
    header: 'Source',
    cell: ({ row }) => (
      <Chip label={row.original.is_ldap_user ? 'LDAP' : 'Local'} variant="outlined" size="small" />
    ),
    size: 100,
  },
  {
    id: 'last_login',
    accessorFn: (row) => dateSortValue(row.last_login),
    header: 'Last Login',
    cell: ({ row }) => dateTime(row.original.last_login, 'Never'),
    size: 170,
  },
  {
    id: 'created_at',
    accessorFn: (row) => dateSortValue(row.created_at),
    header: 'Created',
    cell: ({ row }) => dateTime(row.original.created_at),
    size: 170,
  },
  {
    id: 'updated_at',
    accessorFn: (row) => dateSortValue(row.updated_at),
    header: 'Updated',
    cell: ({ row }) => dateTime(row.original.updated_at),
    size: 170,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const user = row.original;
      return (
        <>
          {/*
            `describeChild` is required wherever a <span> wraps a
            disabled button: MUI otherwise clones `title` onto that
            roleless span as `aria-label`, leaving the button
            nameless and the span in breach of aria-prohibited-attr.
          */}
          <Tooltip
            describeChild
            title={user.is_ldap_user ? 'LDAP users cannot be edited' : 'Edit user'}
          >
            <span>
              <IconButton
                size="small"
                aria-label={`Edit user ${user.username}`}
                onClick={() => onEdit(user)}
                disabled={user.is_ldap_user}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            describeChild
            title={user.is_ldap_user ? 'LDAP passwords managed externally' : 'Change password'}
          >
            <span>
              <IconButton
                size="small"
                aria-label={`Change password for ${user.username}`}
                onClick={() => onChangePassword(user)}
                disabled={user.is_ldap_user}
              >
                <LockIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {user.is_active ? (
            <Tooltip
              describeChild
              title={user.is_ldap_user ? 'LDAP users cannot be deactivated' : 'Deactivate user'}
            >
              <span>
                <IconButton
                  size="small"
                  aria-label={`Deactivate user ${user.username}`}
                  onClick={() => onDeactivate(user.username)}
                  disabled={user.is_ldap_user}
                >
                  <DeactivateIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            // No <span> here, so no describeChild needed: a child's
            // own aria-label wins over the one Tooltip clones.
            <Tooltip title="Reactivate user">
              <IconButton
                size="small"
                aria-label={`Reactivate user ${user.username}`}
                onClick={() => onReactivate(user.username)}
                disabled={user.is_ldap_user}
              >
                <ActivateIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      );
    },
    enableSorting: false,
    size: 160,
    meta: { sticky: 'right' },
  },
];
